// A receipt's page, `settings/billing/receipts/<id>`
// (02-DESIGN/18-billing/02-the-billing-page.md § the tab — Receipts; verb
// table row 119): what the charge was, its period, the lines, the amounts
// with the VAT named by its rate, the status, and the invoice number where
// the operator has written one. The console draws no invoice: Impire sends
// it by email. Read on billing.charges.list, a page at a time, until the id.

import { Link, useParams } from "react-router";

import { NotAllowed, PageHead, Pending, Pill, useLoad } from "../../ui/components.tsx";
import { SERVICE, Subjects, type ChargesListReply } from "../../wire/billing.ts";
import { useSurface } from "../surface.tsx";
import { amountWords, eur, lineWords, rate, RECEIPTS_LINE, statusTone, statusWords } from "./billing.ts";
import { dayOf } from "./plan.ts";
import { billingPath, findCharge } from "./receipts.ts";

export function ReceiptPage() {
  const { id = "" } = useParams();
  const s = useSurface();
  const [charge] = useLoad(`receipt/${id}`, () =>
    findCharge((req) => s.bus.call<ChargesListReply>(SERVICE, Subjects.chargesList(), req), id),
  );
  const crumb = (
    <>
      <Link to={billingPath(s.tenant)}>Billing</Link>
      <span>/</span>
      <span>A receipt</span>
    </>
  );
  if (charge.state === "pending") {
    return (
      <>
        <div className="crumb">{crumb}</div>
        <Pending what="Reading the receipt" />
      </>
    );
  }
  if (charge.state === "refused") {
    return (
      <>
        <PageHead crumb={crumb} title="A receipt" />
        <NotAllowed err={charge.err} what="Receipt" />
      </>
    );
  }
  const c = charge.value;
  if (c === undefined) {
    return (
      <>
        <PageHead crumb={crumb} title="No such receipt" />
        <p className="muted">{s.name} has no receipt with this address.</p>
      </>
    );
  }
  const a = c.amount;
  return (
    <>
      <PageHead crumb={crumb} title={c.description} line={`${dayOf(c.created)} · ${amountWords(a)}`} />
      <div className="card">
        <dl className="kv">
          <dt>Status</dt>
          <dd>
            <Pill tone={statusTone(c.status)}>{statusWords(c.status)}</Pill>
          </dd>
          <dt>Period</dt>
          <dd>
            {dayOf(c.period.start)} to {dayOf(c.period.end)}
          </dd>
          {c.lines.map((l) => (
            <Line
              key={`${l.kind}/${l.name}`}
              label={l.kind === "plan" ? "Plan" : "Add-on"}
              text={lineWords(l)}
            />
          ))}
          <dt>Before VAT</dt>
          <dd>{eur(a.net_cents)}</dd>
          <dt>VAT</dt>
          <dd>
            {a.treatment === "reverse-charge"
              ? "reverse-charged"
              : a.rate_bp > 0
                ? `${eur(a.vat_cents)} at ${rate(a.rate_bp)}`
                : "none"}
          </dd>
          <dt>Total</dt>
          <dd>{eur(a.gross_cents)}</dd>
          <dt>Invoice</dt>
          <dd>
            {c.invoice !== undefined ? (
              <>
                <span className="mono">{c.invoice.number}</span>
                <div className="small faint">Dated {dayOf(c.invoice.date)}</div>
              </>
            ) : (
              "not written yet"
            )}
            <div className="small faint">{RECEIPTS_LINE}.</div>
          </dd>
        </dl>
      </div>
    </>
  );
}

function Line({ label, text }: { label: string; text: string }) {
  return (
    <>
      <dt>{label}</dt>
      <dd>{text}</dd>
    </>
  );
}
